import { Address } from '../vo/address'
import { Benefits } from '../benefits/benefits'
import { ORDER_PAYMENT_STATUS, OrderFromHistoryProps } from './order-history.interface'
import { Observable, interval, map, startWith } from 'rxjs'
import { CoverURL } from '../vo/coverURL'
import { GoogleMapsLink, LivemenuLink } from '../vo/links'

interface OrderHistoryConfig {
	id: string
	intent: string
	originalIntent: string
	venue: {
		name: string
		address: Address
		id: string
		banners: CoverURL[]
		thumbs: CoverURL[]
		googleMaps: GoogleMapsLink
		liveMenu: LivemenuLink
	}
	date: { day: string; month: string; time: string; date: Date }
	reservationDateTime: number
	hasPaymentConfig: boolean
	wasPaid: boolean
	canPay: boolean
	paymentBlocked: boolean
	benefits?: Benefits[]
	voucher?: { value: string }
	voucherBB?: {
		points: number
		code: string
	}
	gratuityPercent: number
	canCancel$: Observable<boolean>
	active: boolean
	points?: number
	payment?: {
		paymentLink?: string
		points: number
		lastFourDigits: string
		code: string
		intialValue: number
		gratuityValue: number
		totalValue: number
		paymentType?: string
	}
	paymentStatus: ORDER_PAYMENT_STATUS
	partySize?: number
	sectionLabel?: string
	removeGratuityValue?: boolean
	isCanceled: boolean
	canceledAt?: Date
	isValidated: boolean
	validatedAt?: Date
	basket?: {
		_id: string
		title: string
		description: string
		descriptionLong: string
		imagePath: string
		enabled: boolean
	}
}

export class OrderHistory {
	private constructor(private config: OrderHistoryConfig) {}

	static create(props: OrderFromHistoryProps): OrderHistory {
		const location = props.venue.address.location
		const venueAddress = new Address(
			props.venue.address.city ?? '',
			props.venue.address.state ?? '',
			props.venue.address.neighborhood ?? '',
			props.venue.address.address ?? '',
			props.venue.address.number ?? ''
		)
		const config: OrderHistoryConfig = {
			id: props._id,
			intent: this.getIntent(props.intent),
			originalIntent: props.intent,
			active:
				(props.status === 'CHK_DONE' || props.status === 'VALIDATED') &&
				(props.paymentStatus === 'READY' ||
					props.paymentStatus === 'NOT_DONE_FAILED' ||
					props.paymentStatus == null),

			// maps mobile and desk
			venue: {
				name: props.venue.name,
				address: venueAddress,
				id: props.venueId,
				banners: props.venue.partner.images.banners.map(url => new CoverURL(url.coverUrl, undefined)),
				thumbs: props.venue.partner.images.thumb.map(url => new CoverURL(url.coverUrl, undefined)),
				googleMaps: new GoogleMapsLink(
					location && location[0],
					location && location[1],
					props.venue.name,
					venueAddress.value
				),
				liveMenu: new LivemenuLink(props.venueId, undefined),
			},
			date: this.getDayAndMonth(
				new Date(props.reservationDay ?? props.createdAt),
				props.reservationTime ?? new Date(props.createdAt).toISOString().slice(11, 16)
			),
			reservationDateTime: this.getReservationDateTime(
				props.reservationDay ?? props.createdAt,
				props.reservationTime ?? new Date(props.createdAt).toISOString().slice(11, 16)
			),
			hasPaymentConfig: props.venueHasPaymentConfig ?? false,
			wasPaid: this.checkWasPaid(props.paymentStatus),
			canPay: this.canPayFn(props.paymentStatus, props.venueHasPaymentConfig ?? false),
			paymentBlocked: this.paymentBlockedFn(props.paymentStatus, props.venueHasPaymentConfig ?? false),
			benefits: props.benefits?.map(benefit => new Benefits(benefit)),
			gratuityPercent: props.gratuityPercent ?? 10,
			canCancel$: interval(1000).pipe(
				startWith(0),
				map(() => {
					const now = Date.now()
					const reservationTime = this.getReservationDateTime(
						props.reservationDay,
						props.reservationTime ?? new Date(props.createdAt).toISOString().slice(11, 16)
					)
					const deadline =
						props.intent === 'checkin' ? reservationTime + 60 * 60 * 1000 : reservationTime

					return (
						props.status !== 'CHK_CANCEL' &&
						!this.checkWasPaid(props.paymentStatus) &&
						now <= deadline
					)
				})
			),
			isCanceled: props.status === 'CHK_CANCEL',
			canceledAt: props.status === 'CHK_CANCEL' ? new Date(props.canceledAt) : undefined,
			isValidated: props.status === 'VALIDATED',
			validatedAt: props.status === 'VALIDATED' ? new Date(props.validatedAt) : undefined,
			points: props.payment?.orderTotalPoints,
			paymentStatus: props.paymentStatus,
			payment: props.payment
				? {
						paymentLink: props.payment?.paymentLink,
						points: props.payment.orderTotalPoints,
						lastFourDigits: props.payment.lastFourDigits,
						code: props.payment.code,
						intialValue: props.payment.orderInitialValue,
						gratuityValue: props.payment.gratuityValue,
						totalValue: props.payment.orderTotalValue,
						paymentType: props.payment.paymentType,
				  }
				: undefined,
			voucher: props.voucher ? { value: props.voucher.value } : undefined,
			voucherBB: props.voucherBB,
			partySize: props.partySize,
			sectionLabel: props.sectionLabel,
			removeGratuityValue: props.removeGratuityValue,
			basket: props.basket,
		}
		return new OrderHistory(config)
	}

	private static getIntent(intent: string): string {
		if (intent === 'reservation') return 'Reserva de Mesa'
		if (intent === 'checkin') return 'CheckIn'
		if (intent === 'waitlist') return 'Fila de Espera'
		if (intent === 'basket') return 'Resgate de cesta'
		return 'Order sem intent'
	}

	private static checkWasPaid(paymentStatus: ORDER_PAYMENT_STATUS): boolean {
		return paymentStatus === 'DONE'
	}

	private static canPayFn(paymentStatus: ORDER_PAYMENT_STATUS, hasPaymentConfig: boolean): boolean {
		return (
			hasPaymentConfig &&
			(paymentStatus === 'READY' || paymentStatus === 'FAILED' || paymentStatus === 'PENDING')
		)
	}

	private static paymentBlockedFn(paymentStatus: ORDER_PAYMENT_STATUS, hasPaymentConfig: boolean): boolean {
		return !hasPaymentConfig || paymentStatus === 'NOT_DONE' || paymentStatus === 'NOT_DONE_FAILED'
	}

	private static getDayAndMonth(date: Date, time: string) {
		const day = String(date.getUTCDate()).padStart(2, '0')
		const month = date.toLocaleString('pt-br', { month: 'short' }).toUpperCase().slice(0, -1)
		return { day, month, date, time }
	}

	private static getReservationDateTime(reservationDay: string, reservationTime: string): number {
		if (!reservationDay) return Infinity
		const [day] = reservationDay.split('T')
		return Date.parse(`${day}T${reservationTime}`)
	}

	get id(): string {
		return this.config.id
	}
	get intent(): string {
		return this.config.intent
	}
	get active(): boolean {
		return this.config.active
	}
	get originalIntent() {
		return this.config.originalIntent
	}
	get venue() {
		return this.config.venue
	}
	get date(): { day: string; month: string; time: string; date: Date } {
		return this.config.date
	}
	get reservationDateTime(): number {
		return this.config.reservationDateTime
	}
	get hasPaymentConfig(): boolean {
		return this.config.hasPaymentConfig
	}
	get wasPaid(): boolean {
		return this.config.wasPaid
	}
	get canPay(): boolean {
		return this.config.canPay
	}
	get paymentBlocked(): boolean {
		return this.config.paymentBlocked
	}
	get benefits(): Benefits[] | undefined {
		return this.config.benefits
	}
	get voucher(): { value: string } | undefined {
		return this.config.voucher
	}
	get voucherBB(): { points: number; code: string } | undefined {
		return this.config.voucherBB
	}
	get gratuityPercent(): number {
		return this.config.gratuityPercent
	}
	get canCancel$(): Observable<boolean> {
		return this.config.canCancel$
	}
	get points(): number | undefined {
		return this.config.points
	}
	get payment():
		| {
				paymentLink?: string
				points: number
				lastFourDigits: string
				code: string
				intialValue: number
				gratuityValue: number
				totalValue: number
				paymentType?: string
		  }
		| undefined {
		return this.config.payment
	}
	get paymentStatus(): ORDER_PAYMENT_STATUS {
		return this.config.paymentStatus
	}
	get partySize(): number | undefined {
		return this.config.partySize
	}
	get sectionLabel(): string | undefined {
		return this.config.sectionLabel
	}
	get removeGratuityValue(): boolean | undefined {
		return this.config.removeGratuityValue
	}
	get isCanceled(): boolean {
		return this.config.isCanceled
	}
	get canceledAt(): Date | undefined {
		return this.config.canceledAt
	}
	get isValidated(): boolean {
		return this.config.isValidated
	}
	get validatedAt(): Date | undefined {
		return this.config.validatedAt
	}
	get hasVoucherBB(): boolean {
		return !!this.config.voucherBB && !!this.config.voucherBB?.code
	}
}
